import { useEffect, useState, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * A campaign's 16:9 banner. Decorative (the campaign name is always rendered
 * next to it), so alt is empty. Without a URL, or when the image fails, it
 * renders nothing, so the card falls back to its banner-less layout.
 * `children` are drawn over the image (e.g. CampaignBannerInfo) and go away
 * with it. `data-campaign-banner` lets a card hide, with CSS `:has()`, the
 * in-body copies of whatever the overlay now shows.
 */
export function CampaignBanner({ src, grayscale = false, className, children }: {
  src?: string | null;
  grayscale?: boolean;
  className?: string;
  children?: ReactNode;
}) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);

  if (!src || !src.trim() || failed) return null;

  return (
    <div
      className={cn('relative aspect-video overflow-hidden bg-muted', className)}
      data-testid="campaign-banner"
      data-campaign-banner=""
    >
      <img
        src={src}
        alt=""
        loading="lazy"
        decoding="async"
        draggable={false}
        onError={() => setFailed(true)}
        className={cn('absolute inset-0 h-full w-full object-cover', grayscale && 'grayscale')}
      />
      {children}
    </div>
  );
}
